// @flow

// $FlowFixMe
import React, { useState, type ElementConfig, useEffect } from 'react';
// $FlowFixMe
import ReactSelect, { createFilter, components } from 'react-select3';
// $FlowFixMe
import ReactSelectAsync from 'react-select3/async';
import classNames from 'classnames';
import Tag from './tag';
import styled from 'styled-components';
import { DropdownArrow } from './dropdown_select/components';
import LoadingSpinner from './loading_spinner';
import XIcon from '../icons/xicon';
import { colors, fontWeight } from '../../lib/constants';

export type DropdownProps = {
  isSearchable: boolean,
  isClearable: boolean,
  isMulti: boolean,
  options?: Array<any>,
  getOptions?: () => Promise<Array<any>>,
  loadOptions?: boolean,
  filterOptions?: Function,
  components: Object,
  styles: Object,
  className?: string,
};

export type DropdownLazyLoadProps = {
  ...DropdownProps,
  getOptions: () => Promise<Array<any>>,
};

const baseHeight = 36;
const borderRadius = 2;

const MultiValueLabel = styled(Tag)`
  padding-right: 25px;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 2px;
  padding-bottom: 2px;

  :first-of-type {
    margin-left: 0;
  }
`;

const dropdownStyles = {
  control: (base, state) => ({
    ...base,
    boxShadow: 'none',
    borderRadius: state.menuIsOpen
      ? `${borderRadius}px ${borderRadius}px 0 0`
      : borderRadius,
    borderColor: colors.lightGrey,
    fontWeight: fontWeight.normal,
    ':hover': {
      borderColor: colors.lightGrey,
    },
  }),
  option: (base, _state) => ({
    ...base,
    color: '#000',
    height: baseHeight,
  }),
  menu: (base, _state) => ({
    ...base,
    boxShadow: 'none',
    borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
    border: `1px solid ${colors.lightGrey}`,
    borderTop: 0,
  }),
  menuList: (base, _state) => ({
    ...base,
    padding: 0,
  }),
  group: (base, _state) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 5,

    ':first-of-type': {
      paddingTop: 10,
    },

    ':last-of-type': {
      paddingBottom: 0,
    },
  }),
  valueContainer: (base, _state) => ({
    ...base,
    padding: '0 10px',
  }),
  multiValue: (base, _state) => ({
    ...base,
    backgroundColor: 'none',
    position: 'relative',
    ':first-of-type': {
      marginLeft: 0,
    },
  }),
  multiValueRemove: (base, _state) => ({
    ...base,
    position: 'absolute',
    paddingTop: 2,
    right: 5,
    top: 0,
    bottom: 0,
    color: colors.mediumGrey,
    ':hover': {
      background: 'none',
      cursor: 'pointer',
    },
  }),
  indicatorsContainer: (base, _state) => ({
    ...base,
    padding: '0px 8px',
  }),
  clearIndicator: (base, _state) => ({
    ...base,
    padding: '0',
  }),
  indicatorSeparator: (_base, _state) => ({
    display: 'none',
  }),
  groupHeading: (base, _state) => ({
    ...base,
    textTransform: 'none',
    fontSize: 14,
  }),
};

const DropdownIndicator = (
  props: ElementConfig<typeof components.DropdownIndicator>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <DropdownArrow menuIsOpen={props.selectProps.menuIsOpen} />
    </components.DropdownIndicator>
  );
};

const LoadingIndicator = (
  props: ElementConfig<typeof components.LoadingIndicator>
) => {
  return <LoadingSpinner {...props} size={'small'} />;
};

const StyledXIcon = styled(XIcon)`
  margin-top: 2px;
`;

const ClearIndicator = (
  props: ElementConfig<typeof components.ClearIndicator>
) => (
  <components.ClearIndicator {...props}>
    <StyledXIcon size={14} color="black" hoverColor="black" />
  </components.ClearIndicator>
);

const defaultProps = {
  isSearchable: false,
  isClearable: false,
  isMulti: false,
  components: {
    MultiValueLabel,
    DropdownIndicator,
    LoadingIndicator,
    ClearIndicator,
  },
  styles: {},
};

const Theme = baseTheme => {
  return {
    borderRadius: borderRadius,
    colors: {
      ...baseTheme.colors,
      neutral20: colors.lightGrey,
      primary25: colors.babyBlue,
      primary50: colors.babyBlue,
      primary: colors.lighterGrey,
    },
    spacing: {
      ...baseTheme.spacing,
      controlHeight: baseHeight,
      menuGutter: 0,
    },
  };
};

const enhanceProps = (props: DropdownProps | DropdownLazyLoadProps) => {
  let enhancedProps = {
    ...props,
    theme: Theme,
    className: classNames(props.className, 'sl-dropdown-container'),
    classNamePrefix: 'sl-dropdown',
    styles: { ...dropdownStyles, ...props.styles },
  };

  enhancedProps = utilizeFastFilterOptions(enhancedProps);

  return enhancedProps;
};

const utilizeFastFilterOptions = (enhancedProps: DropdownProps) => {
  if (enhancedProps.isSearchable && !enhancedProps.filterOptions) {
    enhancedProps.filterOptions = createFilter({ ignoreAccents: false });
  }

  return enhancedProps;
};

// **************
// Wrap with Styled component for some custom things
// **************
const StyledDropdownWrapper = component => {
  return styled(component)`
    // -- custom rule to indent options nested under group --
    .sl-dropdown__group .sl-dropdown__option {
      padding-left: 40px;
    }
  `;
};

const StyledReactSelect = StyledDropdownWrapper(ReactSelect);
const StyledReactSelectAsync = StyledDropdownWrapper(ReactSelectAsync);

// **********
// Components
// **********
const Dropdown = (props: DropdownProps) => {
  useEffect(() => {
    console.log(
      'Dropdown DEPRECATED, use Dropdown from dropdown_select instead.'
    );
  }, []);
  return <StyledReactSelect {...enhanceProps(props)} />;
};
Dropdown.defaultProps = defaultProps;

const AsyncDropdown = (props: DropdownProps) => {
  useEffect(() => {
    console.log(
      'AsyncDropdown DEPRECATED, use Dropdown from dropdown_select instead.'
    );
  }, []);

  return <StyledReactSelectAsync {...enhanceProps(props)} />;
};
AsyncDropdown.defaultProps = defaultProps;

const LazyLoadDropdown = (props: DropdownLazyLoadProps) => {
  useEffect(() => {
    console.log(
      'LazyLoadDropdown DEPRECATED, use Dropdown from dropdown_select instead.'
    );
  }, []);

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [prevGetOptions, setPrevGetOptions] = useState(null);

  // Reset options and loading state if we get a new function for fetching options
  if (props.getOptions !== prevGetOptions) {
    setIsLoading(false);
    setOptions([]);
    // in hooks, to save a function in state it must be wrapped like this because
    // useState setter treats a function argument as lazy calculation
    setPrevGetOptions(() => props.getOptions);
  }

  const fetchOptionsIfNeeded = () => {
    if (options.length === 0 && !isLoading) {
      setIsLoading(true);

      props.getOptions().then(options => {
        setIsLoading(false);
        setOptions(options);
      });
    }
  };

  const open = () => {
    fetchOptionsIfNeeded();
    setMenuIsOpen(true);
  };

  const close = () => {
    setMenuIsOpen(false);
  };

  return (
    <StyledReactSelect
      isLoading={isLoading}
      menuIsOpen={menuIsOpen}
      onMenuOpen={open}
      onMenuClose={close}
      options={options}
      {...enhanceProps(props)}
    />
  );
};
LazyLoadDropdown.defaultProps = defaultProps;

export default Dropdown;
export { AsyncDropdown, LazyLoadDropdown };
